import { Loader } from '@/components/loaders/loader';
import { themeVar } from '@/styles/theme';
import React from 'react';
import styled, { css } from 'styled-components';

interface ButtonProps {
  children: React.ReactNode;
  size?: 'sm' | 'md' | 'lg';
  styleType?: 'default' | 'outline' | 'ghost' | 'transparent' | 'disabled';
  icon?: React.ReactNode;
  secondaryIcon?: React.ReactNode;
  type?: 'button' | 'reset' | 'submit';
  width?: string;
  id?: string;
  isLoading?: boolean;
  loaderColor?: string;
  onClick?: (event?: React.MouseEvent<HTMLButtonElement>) => void;
  className?: string;
  nonInteractive?: boolean;
  'data-testid'?: string;
}

const getSizeClass = (size: ButtonProps['size']) => {
  switch (size) {
    case 'sm':
      return css`
        padding: 10px 20px;
        font-size: 13px;
        line-height: 18.2px;
        gap: 4px;
      `;
    case 'md':
      return css`
        padding: 10px 30px;
        font-size: 16px;
        line-height: 22.4px;
        gap: 6px;
      `;
    case 'lg':
      return css`
        padding: 10px 40px;
        font-size: 19px;
        line-height: 26.6px;
        gap: 8px;
      `;
    default:
      return '';
  }
};

const getTypeClass = (styleType: ButtonProps['styleType']) => {
  switch (styleType) {
    case 'default':
      return css`
        background-color: ${themeVar('action.default')};
        border: 1px solid ${themeVar('action.default')};
        color: ${themeVar('context.light')};
      `;
    case 'outline':
      return css`
        background-color: ${themeVar('layer.base')};
        border: 1px solid ${themeVar('action.default')};
        color: ${themeVar('action.default')};

        &:hover {
          background-color: ${themeVar('action.default')};
          border: 1px solid ${themeVar('action.default')};
          color: ${themeVar('context.light')};

          svg {
            path {
              fill: ${themeVar('context.light')};
            }
          }
        }
      `;
    case 'ghost':
      return css`
        background-color: ${themeVar('layer.base')};
        border: 1px solid ${themeVar('layer.base')};
        color: ${themeVar('action.default')};

        &:hover {
          background-color: ${themeVar('action.default')};
          border: 1px solid ${themeVar('action.default')};
          color: ${themeVar('context.light')};

          svg {
            path {
              fill: ${themeVar('context.light')};
            }
          }
        }
      `;
    case 'transparent':
      return css`
        background-color: transparent;
        color: ${themeVar('action.default')};

        &:hover {
          background-color: ${themeVar('action.default')};
          border: 1px solid ${themeVar('action.default')};
          color: ${themeVar('context.light')};
        }
      `;
    case 'disabled':
      return css`
        border: 1px solid ${themeVar('action.disabled')};
        background-color: ${themeVar('action.disabled')};
        color: ${themeVar('font.disabled')};

        svg {
          path {
            fill: ${themeVar('font.disabled')};
          }
        }
      `;
    default:
      return '';
  }
};

const loaderSizes = {
  sm: 15,
  md: 20,
  lg: 25
};

const Button = ({
  children,
  size = 'sm',
  styleType = 'default',
  icon,
  secondaryIcon,
  type,
  width = 'auto',
  id,
  isLoading = false,
  loaderColor,
  onClick,
  className,
  nonInteractive = false,
  'data-testid': testId
}: ButtonProps) => {
  return (
    <StyledButton
      data-testid={testId || id}
      id={id}
      type={type}
      onClick={onClick}
      disabled={styleType === 'disabled'}
      width={width}
      size={size}
      styleType={styleType}
      className={className}
      nonInteractive={nonInteractive}
    >
      {icon && icon}
      {children}
      {isLoading && <CustomLoader size={loaderSizes[size]} loaderColor={loaderColor} />}
      {secondaryIcon && secondaryIcon}
    </StyledButton>
  );
};

export default Button;

const StyledButton = styled.button<ButtonProps>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: none;
  border-radius: 4px;
  transition: 0.3s ease;
  font-family: Blinker;
  font-style: normal;
  font-weight: 600;
  width: ${({ width }) => width};
  cursor: ${({ styleType, nonInteractive }) => (nonInteractive ? 'default' : styleType === 'disabled' ? 'not-allowed' : 'pointer')};
  pointer-events: ${({ nonInteractive }) => (nonInteractive ? 'none' : 'auto')};

  ${({ size }) => getSizeClass(size)}
  ${({ styleType }) => getTypeClass(styleType)}
`;

const CustomLoader = styled(Loader)`
  padding: 0 !important;
`;
